import { Injectable } from '@angular/core';
import { INavData } from '@coreui/angular-pro';
import { CredoMenuService } from 'credo';

/**
 * Wolfpack Assets menu = the fac3less menu minus My Cards, Media and Bookings.
 *
 * Subtractive on purpose (filter super.createNavItems() rather than returning a
 * hand-written list, the way mnlo/nam3less do): the admin section — including
 * legal_acceptances, which this brand needs because its gate is enabled — is
 * inherited from the shared credo menu and stays in sync with it.
 *
 * The first four removals mirror apps/fac3less; the last two are this brand's.
 * Matching is on the exact full URL, so `/media/info` (Information) survives the
 * `/media/media` (Media) removal.
 *
 * My Cards needs no removal — it is declared in Fac3lessMenuService, not in the
 * shared credo menu, so simply not adding it here is enough. It is dropped
 * because ViaCarte does not run on this brand: User::VIACARTE_SYSTEMS lists only
 * fac3less/globalhealth/mnlo, and config domain 7 is deliberately not added
 * (it would silently fall back to issuing cards under the fac3less system).
 * The ViaCarte admin entry is dropped for the same reason.
 */
@Injectable({ providedIn: 'root' })
export class WolfpackassetsMenuService extends CredoMenuService {
  protected override createNavItems(lang: string, t: (key: string) => string): INavData[] {
    const removed = [
      `/${lang}/user-statistics`,
      `/${lang}/earnings-statistics`,
      `/${lang}/user-tree`,
      `/${lang}/my-payouts`,
      `/${lang}/media/media`,
      `/${lang}/bookings`,
    ];

    const items = super.createNavItems(lang, t).filter(item => !removed.includes(item.url as string));

    // Program Catalog — Wolfpack Assets only, immediately below the Dashboard.
    const catalogItem: INavData = {
      name: t('menu.program_catalog'),
      url: `/${lang}/program-catalog`,
      // cil-school (graduation cap) is registered in IconService.initializeIcons().
      iconComponent: { name: 'cil-school' }
    };
    const dashboardIndex = items.findIndex(item => item.url === `/${lang}/dashboard`);
    items.splice(dashboardIndex >= 0 ? dashboardIndex + 1 : 0, 0, catalogItem);

    // Appointments — the central calendar. This service extends the shared credo
    // menu, not Fac3lessMenuService, so the entries are declared here as well.
    // Placed after Information (Bookings, fac3less's anchor, is removed here).
    const appointments: INavData = {
      name: t('appointments.menu'),
      url: `/${lang}/appointments`,
      iconComponent: { name: 'cil-calendar' }
    };
    const infoIdx = items.findIndex(item => item.url === `/${lang}/media/info`);
    items.splice(infoIdx >= 0 ? infoIdx + 1 : items.length, 0, appointments);

    items.push({
      name: t('appointments.admin_menu'),
      url: `/${lang}/admin/appointments`,
      iconComponent: { name: 'cil-calendar' },
      badge: { color: 'danger', text: 'ADMIN' },
      attributes: { 'data-visible': 'isAdmin' }
    });

    return items;
  }
}
